// Interfaces
import { IPayPeriod, IStatus } from "@/Common/api/types/CommissionCycle";
import { TPositionTreeIconProps } from "@/Washouts/components/PositionTreeIcon";
import { getPayPeriodPositionDisableByAccess, hasPayPeriodPositionInStatus } from "../WashoutReview";
import { resolveDisabledPositionsByStatus, resolveDisabledPositionsByWashoutConditions } from "../resolveDisabledPositionsByStatus";
import { Nullable } from "@/Common/api/types/utilities";
import { IGetPositionDisableToRetractArgs, IGetPositionDisableToApproveArgs } from "@/Washouts/types/WashoutEllipsesMenu";
type TPeriodOrUndefined = IPayPeriod | undefined | null;

const UNKNOWN_STATUS = "UNKNOWN";

const statusRules: Record<string, string> = {
	"PENDING,PENDING": "PENDING",
	"APPROVED,PENDING": "REVIEWING",
	"APPROVED,POSTED_PAYROLL": "APPROVED",
	"POSTED_PAYROLL,POSTED_PAYROLL": "POSTED_PAYROLL",
	"PENDING,POSTED_PAYROLL": "REVIEWING",
	"APPROVED,APPROVED": "APPROVED",
};

type TPositionTreeOptionsProps = TPositionTreeIconProps & {
	itemProps?: {
		selected: boolean;
		reviewed: boolean;
		currentPayPlanAssignmentId: string;
		hasWashout: boolean;
		upcomingPayPlanAssignmentId: string;
		washoutGenerationError: boolean;
	};
	rootLevel?: boolean;
	unReviewable?: boolean;
};

export enum StatusKey {
	NoPayPlan = "NoPayPlan",
	NoPayPlanSigned = "NoPayPlanSigned",
	NoWashout = "NoWashout",
	FailWashout = "FailWashout",
	ApprovedWashout = "ApprovedWashout",
	RetractWashout = "RetractWashout",
	Reviewed = "Reviewed",
	UnReviewable = "UnReviewable",
}

const getPrioritizedStatus = (statusOneObj?: IStatus, statusTwoObj?: IStatus): string => {
	const statusOne = statusOneObj?.status ?? UNKNOWN_STATUS;
	const statusTwo = statusTwoObj?.status ?? UNKNOWN_STATUS;

	if (statusOne === "UNREVIEWABLE" || statusOne === UNKNOWN_STATUS) {
		return statusTwo;
	}
	if (statusTwo === "UNREVIEWABLE" || statusTwo === UNKNOWN_STATUS) {
		return statusOne;
	}
	if (statusOne === "REVIEWING" || statusTwo === "REVIEWING") {
		return "REVIEWING";
	}

	const [statusOneSorted, statusTwoSorted] = [statusOne, statusTwo].sort((statusOneToCompare, statusTwoToCompare) =>
		statusOneToCompare.localeCompare(statusTwoToCompare)
	);
	const key = `${statusOneSorted},${statusTwoSorted}`;
	return statusRules[key] ?? "";
};

const getUniquePositions = (payPeriod: TPeriodOrUndefined, partnerPayPeriod: TPeriodOrUndefined): string[] => {
	const positions = [...(payPeriod?.positionStatus ?? []), ...(partnerPayPeriod?.positionStatus ?? [])].map((status) => status.position);
	return [...new Set(positions)];
};

const getPeriodStatusesCombined = (payPeriod: TPeriodOrUndefined, partnerPayPeriod: TPeriodOrUndefined) => {
	if (!partnerPayPeriod) {
		return (
			payPeriod?.positionStatus?.map((status) => ({
				position: status.position,
				status: status.status,
			})) ?? []
		);
	}

	const positions = getUniquePositions(payPeriod, partnerPayPeriod);
	return positions.map((position) => {
		const periodStatus = payPeriod?.positionStatus.find((item) => item.position === position);
		const partnerStatus = partnerPayPeriod?.positionStatus.find((item) => item.position === position);
		const status = getPrioritizedStatus(periodStatus, partnerStatus);
		return {
			position,
			status,
		};
	});
};

const isSomeProviderCompleted = (payPeriod: Nullable<IPayPeriod>) => {
	const { payPeriodProviders = [] } = payPeriod || {};
	return payPeriodProviders.some((provider) => provider.status === "COMPLETED");
};

const isEveryProviderCompleted = (payPeriod: Nullable<IPayPeriod>) => {
	const { payPeriodProviders = [] } = payPeriod || {};
	if (!payPeriodProviders?.length) {
		return false; // No providers to check
	}
	return payPeriodProviders.every((provider) => provider.status === "COMPLETED");
};

const isEveryPayPeriodSubmitted = (payPeriods: Nullable<IPayPeriod>[]) => {
	if (!payPeriods?.length) {
		return false; // No pay periods to check
	}
	return payPeriods.every((period: Nullable<IPayPeriod>) => period?.status !== "INITIAL");
};

const evaluatePositionStatusConditions = (props: TPositionTreeOptionsProps) => {
	const { itemProps, unReviewable, rootLevel = false } = props;
	const {
		reviewed = false,
		hasWashout = false,
		currentPayPlanAssignmentId = "",
		upcomingPayPlanAssignmentId = "",
		washoutGenerationError = false,
	} = itemProps || {};

	const conditions = [
		{
			condition: rootLevel,
			key: null,
			tooltip: "",
		},
		{
			condition: unReviewable,
			key: StatusKey.UnReviewable,
			tooltip: "Not reviewable position",
		},
		{
			condition: hasWashout && reviewed,
			key: StatusKey.ApprovedWashout,
			tooltip: "",
		},
		{
			condition: washoutGenerationError,
			key: StatusKey.FailWashout,
			tooltip: "Washout failed to generate",
		},
		{
			condition: !hasWashout && (currentPayPlanAssignmentId || upcomingPayPlanAssignmentId),
			key: StatusKey.NoWashout,
			tooltip: "No washout generated yet",
		},
		{
			condition: hasWashout && !currentPayPlanAssignmentId && upcomingPayPlanAssignmentId,
			key: StatusKey.NoPayPlanSigned,
			tooltip: "Pay plan not yet signed",
		},
		{
			condition: !currentPayPlanAssignmentId && !upcomingPayPlanAssignmentId,
			key: StatusKey.NoPayPlan,
			tooltip: "No pay plan assigned",
		},
		{
			condition: hasWashout && !reviewed,
			key: StatusKey.RetractWashout,
			tooltip: "",
		},
	];

	return conditions.find((element) => element.condition);
};

const getPositionDisabledToApprove = (args: IGetPositionDisableToApproveArgs) => {
	const { payPeriod, partnerPayPeriod, accessPositions, disabledByChangeRequests, partnerDisabledByChangeRequests, disabledInSidebar } =
		args;
	let disabledPositions: string[] = [];
	let disabledPartnerPositions: string[] = [];
	const allowedStatuses = ["REVIEWING"];

	if (!payPeriod?.positionStatus?.length && !partnerPayPeriod?.positionStatus?.length) {
		return {
			disabledPositions,
			disabledPartnerPositions,
		};
	}
	const positionStatuses = payPeriod?.positionStatus || [];
	const partnerPositionStatuses = partnerPayPeriod?.positionStatus || [];

	if (positionStatuses?.length) {
		const disabledByAccess = getPayPeriodPositionDisableByAccess(payPeriod, accessPositions);
		const disabledByStatus = resolveDisabledPositionsByStatus({ positionStatuses, allowedStatuses });
		const uniqueDisabledPositions = [
			...new Set([...disabledByChangeRequests, ...disabledByAccess, ...disabledByStatus, ...disabledInSidebar]),
		];
		disabledPositions = [...uniqueDisabledPositions];
	}

	if (partnerPositionStatuses?.length) {
		const disabledByAccess = getPayPeriodPositionDisableByAccess(partnerPayPeriod, accessPositions);
		const disabledByStatus = resolveDisabledPositionsByStatus({ positionStatuses: partnerPositionStatuses, allowedStatuses });
		const uniqueDisabledPositions = [
			...new Set([...partnerDisabledByChangeRequests, ...disabledByAccess, ...disabledByStatus, ...disabledInSidebar]),
		];
		disabledPartnerPositions = [...uniqueDisabledPositions];
	}

	return {
		disabledPositions,
		disabledPartnerPositions,
	};
};

const getPositionDisabledToRetract = (args: IGetPositionDisableToRetractArgs) => {
	const { payPeriod, partnerPayPeriod, accessPositions, washoutConditionsByPosition, disabledInSidebar } = args;
	let disabledPositions: string[] = [];
	let disabledPartnerPositions: string[] = [];
	const allowedStatuses = ["REVIEWING", "APPROVED"];

	if (!payPeriod?.positionStatus?.length && !partnerPayPeriod?.positionStatus?.length) {
		return {
			disabledPositions,
			disabledPartnerPositions,
			hasItems: false,
		};
	}

	const positionStatuses = payPeriod?.positionStatus || [];
	const partnerPositionStatuses = partnerPayPeriod?.positionStatus || [];

	const hasItems = hasPayPeriodPositionInStatus({
		payPeriod,
		washoutConditionsByPosition,
		status: allowedStatuses,
		statusKey: StatusKey.ApprovedWashout,
	});

	const hasItemsPartner = hasPayPeriodPositionInStatus({
		washoutConditionsByPosition,
		payPeriod: partnerPayPeriod,
		status: allowedStatuses,
		statusKey: StatusKey.ApprovedWashout,
	});

	if (positionStatuses?.length) {
		const disabledByAccess = getPayPeriodPositionDisableByAccess(payPeriod, accessPositions);
		const disabledByStatus = resolveDisabledPositionsByStatus({ positionStatuses, allowedStatuses });
		const disabledByWashoutConditions = resolveDisabledPositionsByWashoutConditions({
			positionStatuses,
			washoutConditionsByPosition,
			statusKey: StatusKey.ApprovedWashout,
		});
		const uniqueDisabledPositions = [
			...new Set([...disabledByAccess, ...disabledByStatus, ...disabledByWashoutConditions, ...disabledInSidebar]),
		];
		disabledPositions = [...uniqueDisabledPositions];
	}

	if (partnerPositionStatuses?.length) {
		const disabledByAccess = getPayPeriodPositionDisableByAccess(partnerPayPeriod, accessPositions);
		const disabledByStatus = resolveDisabledPositionsByStatus({ positionStatuses: partnerPositionStatuses, allowedStatuses });
		const disabledByWashoutConditions = resolveDisabledPositionsByWashoutConditions({
			washoutConditionsByPosition,
			positionStatuses: partnerPositionStatuses,
			statusKey: StatusKey.ApprovedWashout,
		});
		const uniqueDisabledPositions = [
			...new Set([...disabledByAccess, ...disabledByStatus, ...disabledByWashoutConditions, ...disabledInSidebar]),
		];
		disabledPartnerPositions = [...uniqueDisabledPositions];
	}

	return {
		disabledPositions,
		disabledPartnerPositions,
		hasItems: hasItems || hasItemsPartner,
	};
};

export {
	getPeriodStatusesCombined,
	getPrioritizedStatus,
	isSomeProviderCompleted,
	isEveryProviderCompleted,
	isEveryPayPeriodSubmitted,
	evaluatePositionStatusConditions,
	getPositionDisabledToApprove,
	getPositionDisabledToRetract,
};
